import { useState, type FormEvent } from 'react';
import {
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Anchor,
  Group,
  Text,
  Title,
} from '@mantine/core';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '../auth/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { AuthLayout } from './auth/AuthLayout.js';

const inputClassNames = { root: 'auth-input' } as const;

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await authApi.login({ email, password });
      await refresh();
      nav('/');
    } catch (err) {
      setError((err as Error).message ?? 'login_failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Title
        order={1}
        fz="clamp(40px, 2.8vw, 52px)"
        fw={700}
        lh={1.05}
        c="#14201B"
        mt={10}
        style={{ letterSpacing: '-0.035em' }}
      >
        Вход в систему
      </Title>
      <Text c="#6B7570" fz={15} mt={8}>
        Используйте корпоративную учётную запись
      </Text>
      <form onSubmit={onSubmit} style={{ marginTop: 22 }}>
        <Stack gap={20}>
          <TextInput
            label="Email"
            type="email"
            size="lg"
            radius="md"
            placeholder="name@eurasia.ru"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            required
            withAsterisk={false}
            classNames={inputClassNames}
          />
          <Stack gap={4}>
            <Group justify="space-between" gap="xs">
              <Text component="label" htmlFor="login-password" fw={600} fz={14} c="#172124">
                Пароль
              </Text>
              <Anchor component={Link} to="/reset-password" fz={14} c="#0C8C7A">
                Забыли пароль?
              </Anchor>
            </Group>
            <PasswordInput
              id="login-password"
              aria-label="Пароль"
              size="lg"
              radius="md"
              placeholder="Введите пароль"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
              withAsterisk={false}
              classNames={inputClassNames}
            />
          </Stack>
          {error && (
            <Alert color="red">
              {error === 'invalid_credentials' ? 'Неверный email или пароль' : error}
            </Alert>
          )}
          <Button
            type="submit"
            loading={loading}
            fullWidth
            h={60}
            radius="md"
            color="#0E3B34"
            fz={16}
            fw={600}
          >
            Войти
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
